import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BnUsuarioService } from "@bndes/bndes-ux4/bn-usuario";
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { ROLE_AGENTE_CONSULTA } from 'src/app/constantes/permissao.constants';
import { PermissaoService } from '../permissao.service';
import { ModalidadeService } from './modalidade.service';
import { MessageChannelService, messageLevel } from "@bndes/bndes-ux4/bndes-angular-utils";
import { ModalidadeGarantia } from '../../model/modalidade_garantia.model';
import { ModalidadeGarantiaService } from '../../features/modalidade_garantia/modalidade-garantia.service';
import { ControleHabilitacao, ControleHabilitacaoCamposService } from '../../features/contratacao/operacao/controle-habilitacao-campos.service';
import { TrancheService } from './tranche.service';

export const DEVE_ESCOLHER_MODALIDADE = "DEVE ESCOLHER MODALIDADE";
export const MODALIDADE_ESCOLHIDA = "MODALIDADE ESCOLHIDA";
export const NAO_POSSUI_ACESSO_A_NENHUMA_MODALIDADE = "NÃO POSSUI ACESSO A NENHUMA MODALIDADE";
export const MODALIDADE_FG2 = 'modalidade-fg2';

/**
 * Este serviço, em conjunto com o escolha-modalidade.guard e o modalidade.service.ts, faz o controle
 * da modalidade selecionada pelo usuário (ou escolhida automaticamente com base em permissões)
 * durante o uso do FG2.
 *
 * Para o agente financeiro, é necessário consultar o backend para verificar quais modalidades estão
 * habilitadas (há um endpoint específico para isso.)
 *
 * Para o gestor, as modalidades habilitadas são obtidas com base nas permissões (N08).
 *
 * Se o usuário que está logado só possui acesso a uma modalidade, esta é automaticamente escolhida.
 * Caso contrário, é redirecionado para a tela de Escolha de Modalidade para realizar a escolha.
 *
 *
 * @author iuryl
 */
@Injectable({
    providedIn: 'root'
})
export class EscolhaModalidadeService {

    private escolhaModalidadeSubject = new BehaviorSubject<string>(undefined);

    private mapaRoles: any;

    public escolhaModalidade$ = this.escolhaModalidadeSubject.asObservable().pipe(
        filter(modalidade => modalidade !== undefined),
    );

    constructor(
        private activatedRoute: ActivatedRoute,
        private httpClient: HttpClient,
        private permissaoService: PermissaoService,
        private usuarioService: BnUsuarioService,
        private modalidadeGarantiaService: ModalidadeGarantiaService,
        private modalidadeService: ModalidadeService,
        private controleHabilitacaoCamposService: ControleHabilitacaoCamposService,
        private trancheService: TrancheService,
        protected messageService: MessageChannelService
    ) {
        this.modalidadeGarantiaService.buscarModalidades().subscribe((loteModalidades) => {
            this.modalidadeService.setModalidades(loteModalidades.modalidades);
            this.mapaRoles = this.inverteChavesValores(loteModalidades.mapaRoles);
            this.controleHabilitacaoCamposService.setControleHabilitacao(this.criaControleHabilitacao(loteModalidades.mapaFeaturesPassiveisAditivo, loteModalidades.mapaFeaturesPassiveisRetificacao));
            this.inicializa();
          });
    }

    private inicializa() {
        this.carregarModalidade();

        this.usuarioService.usuario$.pipe(
            distinctUntilChanged(),
            filter(usuario => !usuario)
        ).subscribe(() => {
            localStorage.removeItem(MODALIDADE_FG2);
            this.escolhaModalidadeSubject.next(null);
            this.modalidadeService.escolherModalidade(null);
        });
    }

    private criaControleHabilitacao(mapaFeaturesPassiveisAditivo, mapaFeaturesPassiveisRetificacao): ControleHabilitacao[] {
        let result = [];
        Object.keys(mapaFeaturesPassiveisAditivo).forEach((campo) => {
            let controle = new ControleHabilitacao();
            controle.campo = campo;
            controle.passivelAditivo = mapaFeaturesPassiveisAditivo[campo];
            controle.passivelRetificacao = mapaFeaturesPassiveisRetificacao[campo];
            result.push(controle);
        });
        return result;
    }

    private inverteChavesValores(obj) {
        let result = {};
        Object.keys(obj).forEach((k) => result[obj[k]] = k);
        return result;
    }

    private carregarModalidade() {
        const rolesModalidades = this.modalidadeService.getModalidades().map((m) => this.mapaRoles[m.roleAssociada]);
         // console.log('rolesModalidades ' + rolesModalidades);
        this.permissaoService.getPermissoesUsuarioAutenticado().pipe(
            //obtém modalidades habilitadas, dependendo do tipo de usuário (agente ou gestor)
            switchMap((permissoes) => {
                if (permissoes && permissoes[ROLE_AGENTE_CONSULTA]) {
                // agente financeiro
                    //em tese, seria possível já fazer esse filtro das modalidades habilitadas no back,
                    //tentei, mas acabei gerando um loop infinito, será que é possível?
                    return this.buscaModalidadesHabilitadas().pipe(
                        map(modarr => {
                            return modarr
                                .filter(objmod => {
                                    return objmod && permissoes[this.mapaRoles[objmod.roleAssociada]];
                                }).map(objmod => objmod.sigla);
                        })
                    );
                } else {
                // gestor
                    return of(this.modalidadeService.getModalidades().filter(objmod => permissoes[this.mapaRoles[objmod.roleAssociada]]).map(m => m.sigla));
                }
            }),
        ).subscribe(modalidadesComPermissao => {
            if (!modalidadesComPermissao || modalidadesComPermissao.length == 0)    {
                this.messageService.addMessage({
                    title: 'Atenção',
                    type: 'CRUD_OPERATION',
                    level: messageLevel.ERROR,
                    message: 'O usuário sem modalidade habilitada.',
                    timeout: 10000
                });
            }

            console.debug('modalidades em que usuário tem permissão: ' + modalidadesComPermissao);
            //se possui mais de uma modalidade habilitada, deve escolher
            //a não ser que já tenha escolhido antes (estará registrada no localStorage)
            if (modalidadesComPermissao.length > 1) {
                const modalidade = localStorage.getItem(MODALIDADE_FG2);
                if (modalidade) {
                    this.associarModalidade(modalidade);
                } else {
                    // removendo da lista de modalidades visível na tela aquelas em que o usuário atual não possui permissão
                    this.modalidadeService.setModalidades(
                        this.modalidadeService.getModalidades().filter(objmod => modalidadesComPermissao.includes(objmod.sigla))
                    );
                    this.modalidadeService.setModalidadesAssincrono(this.modalidadeService.getModalidades().filter(objmod => modalidadesComPermissao.includes(objmod.sigla)));
                    this.escolhaModalidadeSubject.next(DEVE_ESCOLHER_MODALIDADE);

                }
            //se possui apenas uma modalidade habilitada, esta é automaticamente escolhida
            } else if (modalidadesComPermissao.length === 1) {
                this.associarModalidade(modalidadesComPermissao[0]);
            //não possui modalidades habilitadas --> usuário sem acesso ao sistema
            } else {
                this.escolhaModalidadeSubject.next(NAO_POSSUI_ACESSO_A_NENHUMA_MODALIDADE);
            }
        });
    }

    /**
     * Antes de prosseguir com o uso do sistema, é necessário que a sessão do usuário
     * seja atribuída a uma modalidade (que ficará vigente até o fim da sessão).
     *
     * @param modalidade a qual modalidade a sessão do usuário será atribuída
     */
    public associarModalidade(modalidade: string) {
        this.escolhaModalidadeSubject.next(MODALIDADE_ESCOLHIDA);
        this.httpClient.post(`rest/front/seguranca/${modalidade}/atribuirModalidade`, {}).pipe(
            tap(() => console.log('Modalidade atribuída ao usuário.')),
            map(() => modalidade)
        ).subscribe(modalidade => {
            localStorage.setItem(MODALIDADE_FG2, modalidade);
            if (this.trancheService.jaCarregouTranches()
                && this.trancheService.siglaModalidadeGarantiaTranchesCarregadas() != modalidade
                && this.activatedRoute.snapshot['_routerState'].url != '/modalidade-garantia') {
                location.reload();
            }
            else {
                this.modalidadeService.escolherModalidade(modalidade);
            }
        });
    }

    private buscaModalidadesHabilitadas(): Observable<ModalidadeGarantia[]> {
        return this.httpClient.get(`rest/front/seguranca/modalidadesHabilitadas`).pipe(
          catchError((e) => { console.error(e); throw e})) as Observable<ModalidadeGarantia[]>;
       }
}
